import React, { useMemo, useState } from "react";
import { countsAsRevenue } from "../../utils/orderMoney";
import { Order } from "./types";

// Top Fans — repeat requesters grouped by email / Instagram / nickname, with
// real-money spend only. Moved out of pages/AdminDjProfile.tsx in the Phase C
// split; the card renders nothing until someone has requested twice.
export function DjTopFans({ orders }: { orders: Order[] }) {
  const [showTopFans, setShowTopFans] = useState(false);

  // Top fans: group by customer identifier, filter count > 1
  const topFans = useMemo(() => {
    const map = new Map<string, {
      key: string;
      email: string | null;
      name: string | null;
      nickname: string | null;
      allNicknames: Set<string>;
      instagram: string | null;
      cardLast4: string | null;
      count: number;
      totalSpent: number;
      lastRequest: string;
    }>();

    for (const o of orders) {
      const identifier = o.customerEmail?.toLowerCase().trim()
        || o.clientInstagram?.toLowerCase().trim()
        || o.clientNickname?.trim()
        || null;
      if (!identifier) continue;

      // "Total Spent" is real money only — unpaid selections (and refunded /
      // declined orders) don't count toward a fan's spend, though they still
      // count toward their request total.
      const spent = countsAsRevenue(o) ? (o.amount || 0) : 0;

      const existing = map.get(identifier);
      if (existing) {
        existing.count++;
        existing.totalSpent += spent;
        if (o.clientNickname) existing.allNicknames.add(o.clientNickname);
        if (o.createdAt > existing.lastRequest) {
          existing.lastRequest = o.createdAt;
          if (o.clientNickname) existing.nickname = o.clientNickname;
        }
        if (!existing.name && o.customerName) existing.name = o.customerName;
        if (!existing.email && o.customerEmail) existing.email = o.customerEmail;
        if (!existing.instagram && o.clientInstagram) existing.instagram = o.clientInstagram;
        if (!existing.cardLast4 && o.cardLast4) existing.cardLast4 = o.cardLast4;
      } else {
        const nickSet = new Set<string>();
        if (o.clientNickname) nickSet.add(o.clientNickname);
        map.set(identifier, {
          key: identifier,
          email: o.customerEmail || null,
          name: o.customerName || null,
          nickname: o.clientNickname || null,
          allNicknames: nickSet,
          instagram: o.clientInstagram || null,
          cardLast4: o.cardLast4 || null,
          count: 1,
          totalSpent: spent,
          lastRequest: o.createdAt,
        });
      }
    }

    return Array.from(map.values())
      .filter((f) => f.count > 1)
      .sort((a, b) => b.count - a.count || b.totalSpent - a.totalSpent);
  }, [orders]);

  return (
    <>
      {topFans.length > 0 && (
        <div className="adm-card adm-mb-16">
          <div
            className="card-header-flex"
            style={{ cursor: "pointer", marginBottom: showTopFans ? 12 : 0 }}
            onClick={() => setShowTopFans((v) => !v)}
          >
            <h2 className="adm-section-title">
              <span style={{ display: "inline-block", transition: "transform 0.2s", transform: showTopFans ? "rotate(90deg)" : "rotate(0deg)", fontSize: "0.7rem", marginRight: 8 }}>▶</span>
              Top Fans ({topFans.length})
            </h2>
          </div>
          {showTopFans && <div className="adm-table-wrapper">
            <table className="adm-table-light adm-table-compact">
              <thead>
                <tr>
                  <th style={{ width: "5%" }}>#</th>
                  <th style={{ width: "22%" }}>Fan</th>
                  <th style={{ width: "22%" }}>Email</th>
                  <th style={{ width: "17%" }}>Instagram / Nickname</th>
                  <th style={{ width: "10%" }}>Requests</th>
                  <th style={{ width: "12%" }}>Total Spent</th>
                  <th style={{ width: "12%" }}>Last Request</th>
                </tr>
              </thead>
              <tbody>
                {topFans.map((f, i) => (
                  <tr key={f.key} className="hover-row">
                    <td className="muted-text adm-semibold">{i + 1}</td>
                    <td
                      className="adm-semibold adm-fs-82"
                      title={[f.cardLast4 ? `•••• ${f.cardLast4}` : null].filter(Boolean).join("") || undefined}
                    >
                      {f.name || f.key}
                    </td>
                    <td className="adm-clip-cell">
                      <span
                        className="muted-text adm-ellipsis adm-fs-76"
                      >
                        {f.email || "—"}
                      </span>
                    </td>
                    <td className="adm-fs-78">
                      {(() => {
                        const others = Array.from(f.allNicknames).filter((n) => n !== (f.instagram || f.nickname));
                        const hoverText = others.length > 0 ? `Previous: ${others.join(", ")}` : undefined;
                        if (f.instagram) {
                          return (
                            <a href={`https://instagram.com/${f.instagram}`} target="_blank" rel="noreferrer" className="adm-ig-link" title={hoverText}>
                              @{f.instagram}
                            </a>
                          );
                        }
                        if (f.nickname) {
                          return <span title={hoverText}>{f.nickname}</span>;
                        }
                        return <span className="muted-text">—</span>;
                      })()}
                    </td>
                    <td className="adm-bold">{f.count}</td>
                    <td className="adm-semibold adm-green">
                      ${f.totalSpent.toFixed(2)}
                    </td>
                    <td className="muted-text adm-fs-76 adm-nowrap">
                      {new Date(f.lastRequest).toLocaleDateString()}{" "}
                      {new Date(f.lastRequest).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
        </div>
      )}
    </>
  );
}
